import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTableType } from '../graphql/types';
import { createTeachingBlockVersionTableColumns } from '../utils/TeachingBlockVersionTableUtils';

type TeachingBlockVersionRoomsTableProps = {
  versionUuid: string | undefined;
  search: string;
  versionRoomUuids: string[];
  onRowSelection: (rowData: TeachingBlockVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
};

export const TeachingBlockVersionRoomsTable = observer(
  ({
    onRowSelection,
    onShowLessonClick,
    versionUuid,
    search,
    versionRoomUuids,
  }: TeachingBlockVersionRoomsTableProps) => {
    const { t } = useTranslation();

    const { sorting, saveSorting } = useColumnsSort('teachingBlockVersionRoomsTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const columns = useMemo(() => createTeachingBlockVersionTableColumns(t('rooms.name')), [t]);

    const store = useTeachingBlockStore();

    //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
    const createActionItems = useCallback((_row: Row<TeachingBlockVersionTableType>): DropdownMenuItem[] => {
      return [
        {
          label: t('teachingBlockVersion.editData'),
        },
        {
          label: t('teachingBlockVersion.test'),
        },
        {
          label: t('teachingBlockVersion.remove'),
          type: 'default',
          color: 'error',
        },
      ];
    }, []);

    const { roomsData } = useLoadBasicData({ pause: !versionUuid });

    const roomsTableData: TeachingBlockVersionTableType[] = useMemo(() => {
      return (
        roomsData?.rooms
          .filter((c) => versionRoomUuids.includes(c.uuid))
          .map((c) => {
            return {
              color: {},
              count: 0,
              name: c.name,
              shortName: c.roomNumber ?? '',
              uuid: c.uuid,
            };
          }) ?? []
      );
    }, [roomsData?.rooms, versionRoomUuids]);

    return (
      <>
        <Table<TeachingBlockVersionTableType>
          globalFilter={search}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={roomsTableData}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('teachingBlockVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  disabled={store.readonly}
                  trigger={
                    <Button
                      disabled={store.readonly}
                      hierarchy='secondary'
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={createActionItems(row)} />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          rowSelection={rowSelection}
          showSelected
        />
      </>
    );
  },
);
